import { PropTypes } from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import CreateTemplateModal from "./components/CreateTemplateModal";
import TemplateSelect from "./components/TemplateSelect";
import { getCurrentUserId, isAdminUser } from "./helpers/currentUser";
import useTemplateManager from "./hooks/useTemplateManager";

const SmsTemplateSelect = ({
  body = "",
  media_url = "",
  media_name = "",
  applyTemplate = () => false,
  validateSmsTemplate = () => ({}),
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = isAdminUser();
  const currentUserId = getCurrentUserId();

  const formState = useMemo(
    () => ({ body, media_url, media_name }),
    [body, media_url, media_name],
  );

  const isDifferent = useCallback((currentFormState, template) => {
    const bodyChanged = currentFormState.body !== template.body;
    const mediaChanged =
      currentFormState.media_url !== template.media_url ||
      currentFormState.media_name !== template.media_name;
    return bodyChanged || mediaChanged;
  }, []);

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    templateChanged,
    setTemplateChanged,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isCopying,
    isSharing,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    shareTemplate,
    unshareTemplate,
    applyTemplate: trackApply,
  } = useTemplateManager({
    apiPath: "/sms_templates",
    apiResourceName: "sms_template",
    templateType: "sms",
    displayNameSingular: "SMS Template",
    displayNamePlural: "SMS Templates",
    formState,
    isDifferent,
    onTemplateCreated: () => {
      setShowCreateModal(false);
    },
  });

  const handleCreate = (payload) => {
    const errors = validateSmsTemplate();
    if (Object.keys(errors).length === 0) {
      createTemplate(() => payload);
    }
  };

  const updateSmsTemplate = () => {
    const errors = validateSmsTemplate();
    if (Object.keys(errors).length === 0 && selectedTemplate?.value) {
      updateTemplate(() => ({
        id: selectedTemplate.value,
        body,
        media_url,
        media_name,
      }));
    }
  };

  const handleOpenCreate = () => {
    const errors = validateSmsTemplate();
    if (Object.keys(errors).length === 0) {
      setShowCreateModal(true);
    }
  };

  return (
    <>
      <TemplateSelect
        templates={templates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        templateChanged={templateChanged}
        setTemplateChanged={setTemplateChanged}
        isLoading={isLoading}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        isCopying={isCopying}
        isSharing={isSharing}
        updateTemplate={updateSmsTemplate}
        deleteTemplate={deleteTemplate}
        copyTemplate={copyTemplate}
        shareTemplate={shareTemplate}
        unshareTemplate={unshareTemplate}
        trackApply={trackApply}
        applyTemplate={applyTemplate}
        onCreate={handleOpenCreate}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        infoTooltip="Templates save your message body."
      />
      <CreateTemplateModal
        templateType="sms"
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isCreating={isCreating}
        isAdmin={isAdmin}
        formValues={{ body, media_url, media_name }}
      />
    </>
  );
};

SmsTemplateSelect.propTypes = {
  body: PropTypes.string,
  media_url: PropTypes.string,
  media_name: PropTypes.string,
  applyTemplate: PropTypes.func,
  validateSmsTemplate: PropTypes.func,
};

export default SmsTemplateSelect;
